import { API } from '@/src/api/api'
import { queryKeys } from '@/src/api/queryKeys'
import type { IFetchOneVisualizationResponse } from '@/src/api/types/backend/visualization.api'
import { useSeriesContext } from '@/src/contexts/SeriesContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { ONE_MINUTE_IN_MS } from '@/src/utils/constants'
import { VisualizationDataViewEnum } from '@features/analytics/enums/visualizationDataView.enum'
import { useQuery } from '@tanstack/react-query'
import { subDays } from 'date-fns'
import { useCallback } from 'react'
import type { IVisualizationData } from '../../types/visualizationData.interface'

const initialData: IVisualizationData = {
  id: '',
  formData: {
    defaultView: VisualizationDataViewEnum.TABLE,
    name: '',
    visualizationDateRange: [subDays(new Date(), 1).toISOString(), new Date().toISOString()],
    granularity: 'loadProfile',
    timeSeriesIds: [],
  },
  graphData: {
    granularity: '',
    leftYAxisLabel: '',
    rightYAxisLabel: '',
    dataPoints: [],
  },
  tableData: {
    granularity: '',
    rows: [],
  },
  kpiData: {},
}

export const granularityValueMap = {
  [GranularityEnum.FIFTEEN_MINUTES]: 15,
  [GranularityEnum.THIRTY_MINUTES]: 30,
  [GranularityEnum.ONE_HOUR]: 60,
  [GranularityEnum.ONE_DAY]: 1440,
  [GranularityEnum.ONE_WEEK]: 10080,
  [GranularityEnum.ONE_MONTH]: 43200,
}

export const useVisualizationData = ({ id }: { id?: string }) => {
  const { series } = useSeriesContext()
  const toast = useBaseToast()

  const queryKey = queryKeys.analytics.visualizations.oneById.toKeyWithArgs(id)
  const query = useQuery<any, any, IVisualizationData>({
    queryKey,
    enabled: !!id,
    staleTime: ONE_MINUTE_IN_MS * 5,
    retry: 0,

    /*  The return value of this function will be cached by react-query */
    queryFn: async () => {
      if (!id) return null
      try {
        const response = await API.visualization.fetchOne({ id })
        return response.data
      } catch (err) {
        toast({
          id: 'loading-visualization-error',
          status: 'error',
          description: 'Fehler beim Laden der Visualisierung',
        })
        throw err
      }
    },

    /* The return value of this function will NOT be cached by react-query */
    select: useCallback(
      (data: IFetchOneVisualizationResponse): IVisualizationData => {
        if (!series.length) return initialData

        // @ts-ignore
        const { parameters, id, kpis, seriesData, units } = data
        //TODO: Fix the units, they are not being returned correctly
        const dataPoints = seriesData.map((dataPoint: any) => ({
          date: new Date(dataPoint.date),
          timeSeries1: dataPoint.timeSeries1,
          timeSeries2: dataPoint.timeSeries2,
        }))

        const series1 = series.find(s => parameters.timeseries[0]?.id && s.id === parameters.timeseries[0].id)
        const series2 = series.find(s => parameters.timeseries[1]?.id && s.id === parameters.timeseries[1].id)

        const timeSeries1Name = series1?.label || ''
        const timeSeries1Unit = units.series1
        const timeSeries2Name = series2?.label || ''
        const timeSeries2Unit = units.series2

        const series1Kpis = kpis.series1
        const series2Kpis = kpis.series2

        const visualizationData: IVisualizationData = {
          id,
          formData: {
            defaultView: parameters.chart_type,
            name: parameters.title,
            granularity: [GranularityEnum.FIFTEEN_MINUTES, GranularityEnum.THIRTY_MINUTES, GranularityEnum.ONE_HOUR].includes(
              parameters.granularity
            )
              ? 'loadProfile'
              : parameters.granularity,
            visualizationDateRange: [String(parameters.timeframe_start), String(parameters.timeframe_end)] as [string, string],
            timeSeriesIds: parameters.timeseries.map((timeseries: any) => timeseries.id),
          },
          kpiData: {
            // @ts-ignore
            timeSeries1: { unit: units.series1.kpi, name: timeSeries1Name, ...series1Kpis },
            // @ts-ignore
            timeSeries2: series2Kpis ? { unit: units.series2.kpi, name: timeSeries2Name, ...series2Kpis } : null,
            correlationCoefficient: kpis.correlationCoefficient,
          },
          graphData: {
            leftYAxisLabel: timeSeries1Unit.entries,
            rightYAxisLabel: timeSeries2Unit?.entries,
            timeSeries1Name,
            timeSeries2Name,
            timeSeries1Unit,
            timeSeries2Unit,
            granularity: parameters.granularity,
            dataPoints,
          },
          tableData: {
            timeSeries1Name,
            timeSeries2Name,
            timeSeries1Unit,
            timeSeries2Unit,
            granularity: parameters.granularity,
            rows: dataPoints,
          },
        }

        return visualizationData
      },
      [id, series]
    ),
  })

  return { ...query, data: query.data ?? initialData }
}
